// Mobile touch controls: left virtual joystick (throttle + turn, analog),
// right PEDAL hold-button, stone-throw, camera and scoreboard toggles.
// Only mounts on touch devices (or with ?touch=1 for testing).

export function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 ||
    new URLSearchParams(location.search).has('touch');
}

const DEAD = 0.18;
function dead(v) {
  const a = Math.abs(v);
  if (a < DEAD) return 0;
  return Math.sign(v) * Math.min(1, (a - DEAD) / (1 - DEAD));
}

// hooks: { onThrow() }
export function buildTouch(hooks) {
  // NOTE: throttle/turn stay RAW analog here. main.js ramps the keyboard input
  // instead and skips the ramp while state.active — never ramp twice.
  const state = {
    active: false, pedal: false, throttle: 0, turn: 0, camFar: false, board: false,
    stones: 0, setStones() { /* no-op off touch devices */ },
  };
  if (!isTouchDevice()) return state;
  state.active = true;
  document.body.classList.add('touch');

  const ui = document.createElement('div');
  ui.id = 'touchui';
  ui.innerHTML = `
    <div id="tjoy"><div id="tknob"></div></div>
    <div class="tbtn" id="tpedal">PEDAL</div>
    <div class="tbtn" id="tstone">🪨×0</div>
    <div class="tbtn tsmall" id="tcam">📷</div>
    <div class="tbtn tsmall" id="tboard">🏆</div>
  `;
  document.body.appendChild(ui);

  // --- joystick
  const joy = ui.querySelector('#tjoy');
  const knob = ui.querySelector('#tknob');
  let joyId = null, cx = 0, cy = 0, R = 1;

  function joyMove(e) {
    let dx = (e.clientX - cx) / R, dy = (e.clientY - cy) / R;
    const len = Math.hypot(dx, dy);
    if (len > 1) { dx /= len; dy /= len; }
    knob.style.transform = `translate(${dx * R * 0.55}px, ${dy * R * 0.55}px)`;
    state.turn = -dead(dx);      // right on stick = turn right (negative yaw rate)
    state.throttle = -dead(dy);  // up on stick = forward
  }
  function joyReset() {
    joyId = null;
    knob.style.transform = 'translate(0,0)';
    state.turn = 0; state.throttle = 0;
  }
  joy.addEventListener('pointerdown', e => {
    e.preventDefault();
    joyId = e.pointerId;
    joy.setPointerCapture(joyId);
    const r = joy.getBoundingClientRect();
    cx = r.left + r.width / 2; cy = r.top + r.height / 2; R = r.width / 2;
    joyMove(e);
  });
  joy.addEventListener('pointermove', e => { if (e.pointerId === joyId) joyMove(e); });
  joy.addEventListener('pointerup', e => { if (e.pointerId === joyId) joyReset(); });
  joy.addEventListener('pointercancel', e => { if (e.pointerId === joyId) joyReset(); });

  // --- pedal (hold)
  const pedal = ui.querySelector('#tpedal');
  pedal.addEventListener('pointerdown', e => {
    e.preventDefault();
    state.pedal = true;
    pedal.classList.add('held');
    pedal.setPointerCapture(e.pointerId);
  });
  for (const ev of ['pointerup', 'pointercancel']) {
    pedal.addEventListener(ev, () => { state.pedal = false; pedal.classList.remove('held'); });
  }

  // --- taps
  const stoneBtn = ui.querySelector('#tstone');
  stoneBtn.addEventListener('pointerdown', e => { e.preventDefault(); hooks.onThrow(); });
  // the button is a counter, not a lamp: you can stack up to three stones
  state.setStones = n => {
    const c = Math.max(0, n | 0);
    if (c === state.stones) return;
    state.stones = c;
    stoneBtn.textContent = `🪨×${c}`;
    stoneBtn.classList.toggle('held', c > 0);
  };
  ui.querySelector('#tcam').addEventListener('pointerdown', e => {
    e.preventDefault();
    state.camFar = !state.camFar;
    e.currentTarget.classList.toggle('held', state.camFar);
  });
  ui.querySelector('#tboard').addEventListener('pointerdown', e => {
    e.preventDefault();
    state.board = !state.board;
    e.currentTarget.classList.toggle('held', state.board);
  });

  return state;
}

// Best-effort immersion on phones; both calls may be refused — that's fine.
export function requestImmersive() {
  try { document.documentElement.requestFullscreen?.()?.catch(() => {}); } catch { /* ignore */ }
  try { screen.orientation?.lock?.('landscape')?.catch(() => {}); } catch { /* ignore */ }
}
